/**
 * @module AlgoliaAllProducts
 * @description Initializes and manages Algolia product search grid for all product types.
 *
 * Combines robots, robodrills, and CNCs into a unified searchable grid.
 * Used on industry pages to show all products related to that industry.
 */

import { createProductSearchGrid } from './BaseProductSearchGrid.js';
import { createProductConfig } from '@js/algolia/createProductConfig.js';
import { allProductsConfig } from '@js/config/algolia.js';

/**
 * Get array of specs to display based on product type
 * @param {Object} result - The search result
 * @returns {Array} Array of spec objects with icon, label, value, and unit
 */
function getSpecs(result) {
  const specs = [];

  switch (result.productType) {
    case 'robot':
      if (result.maxPayload) {
        specs.push({
          icon: 'max-payload',
          label: 'Max Payload',
          value: result.maxPayload,
          unit: ' kg',
        });
      }
      if (result.reach) {
        specs.push({
          icon: 'reach',
          label: 'Reach',
          value: result.reach,
          unit: ' mm',
        });
      }
      break;

    case 'robodrill':
      if (result.tableSizeX && result.tableSizeY) {
        specs.push({
          icon: null,
          label: 'Table Size',
          value: `${result.tableSizeX} × ${result.tableSizeY}`,
          unit: ' mm',
        });
      }
      if (result.numberOfTools) {
        specs.push({
          icon: null,
          label: 'Tools',
          value: result.numberOfTools,
          unit: '',
        });
      }
      break;

    case 'cnc':
      if (result.maxAxesPerPath) {
        specs.push({
          icon: 'axes',
          label: 'Max Axes/Path',
          value: result.maxAxesPerPath,
          unit: '',
        });
      }
      break;
  }

  return specs;
}

/**
 * Check if product has any specs to display
 * @param {Object} result - The search result
 * @returns {boolean}
 */
function hasSpecs(result) {
  switch (result.productType) {
    case 'robot':
      return !!(result.maxPayload || result.reach);
    case 'robodrill':
      return !!(result.tableSizeX || result.numberOfTools);
    case 'cnc':
      return !!result.maxAxesPerPath;
    default:
      return false;
  }
}

/**
 * Get category label based on product type
 * @param {Object} result - The search result
 * @returns {string|null}
 */
function getCategoryLabel(result) {
  switch (result.productType) {
    case 'robot':
      return result.robotType && result.robotType.length
        ? result.robotType[0]
        : 'Robot';
    case 'robodrill':
      return 'ROBODRILL';
    case 'cnc':
      return 'CNC';
    case 'robotAndGo':
      return 'Robot & Go';
    default:
      return result.productTypeLabel || null;
  }
}

/**
 * Format Algolia result to match product-card component structure
 *
 * This function remaps Algolia search result fields to match the structure expected by
 * the product-card component. Handles all product types (robot, robodrill, cnc).
 *
 * @param {Object} result - The raw Algolia search result object
 * @returns {Object} Formatted data matching product-card structure
 */
function formatCardData(result) {
  return {
    heading: result.title || '',
    url: result.absoluteUri || result.url || '#',
    image: result.productImage || null,
    category: getCategoryLabel(result),
    description: result.description || result.tagline || null,
    attributes: getSpecs(result).map((spec) => ({
      label: spec.label,
      value: spec.value + spec.unit,
      icon: spec.icon,
    })),
    legacy: result.legacy || false,
    productType: result.productType,
  };
}

// Create and export the Alpine component using the base factory
export default createProductSearchGrid({
  appId: allProductsConfig.app_id,
  apiKey: allProductsConfig.api_key,
  indexName: allProductsConfig.index,
  configureWidget: createProductConfig('[data-search-all-products]'),
  formatCardData,
  getSpecs,
  hasSpecs,
  scrollToSelector: '[data-search-all-products]',
});
